import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MonstersDashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function MonstersDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState('live');
  const [historyFilter, setHistoryFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/monsters`);
      setData(response.data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load monsters data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="monsters-loading">Loading monster predictions…</div>;
  if (error) return <div className="monsters-error">⚠️ {error}</div>;
  if (!data) return <div className="monsters-loading">No data available</div>;

  const live = data.live_picks || [];
  const history = data.history || [];
  const weekly = data.weekly || [];
  const agg = data.aggregate || {};

  const liveCalls = live.filter(p => p.side === 'call').sort((a, b) => parseFloat(b.tilted_score || 0) - parseFloat(a.tilted_score || 0));
  const livePuts = live.filter(p => p.side === 'put').sort((a, b) => parseFloat(b.p_put || 0) - parseFloat(a.p_put || 0));

  const filteredHistory = history.filter(r => {
    if (historyFilter === 'all') return true;
    if (historyFilter === 'wins5') return (r.out_max_up_7d || 0) >= 5;
    if (historyFilter === 'wins15') return (r.out_max_up_7d || 0) >= 15;
    if (historyFilter === 'monsters') return (r.out_max_up_7d || 0) >= 30;
    if (historyFilter === 'losses') return (r.out_max_up_7d || 0) < 0;
    return true;
  });

  const fmtPct = (v) => v == null ? '—' : `${v >= 0 ? '+' : ''}${parseFloat(v).toFixed(1)}%`;
  const cls = (v) => v == null ? '' : (v >= 0 ? 'pos' : 'neg');

  return (
    <div className="monsters-dashboard">
      <div className="monsters-header">
        <h2>🐲 Monster Picks</h2>
        <div className="monsters-meta">
          <span>Generated: {data.generated_at}</span>
          <span>Latest: <strong>{data.latest_date}</strong></span>
          <button onClick={fetchData} className="refresh-btn">🔄 Refresh</button>
        </div>
      </div>

      {/* Headline stats */}
      {agg.total_calls > 0 && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Backtest Picks</div>
            <div className="stat-value">{agg.total_calls}</div>
            <div className="stat-sub">total call picks</div>
          </div>
          <div className="stat-card hit">
            <div className="stat-label">5%+ Hit Rate</div>
            <div className="stat-value">{agg.hit_rate_5pct}%</div>
            <div className="stat-sub">{agg.hits_5pct} / {agg.total_calls}</div>
          </div>
          <div className="stat-card hit-mid">
            <div className="stat-label">15%+ Hit Rate</div>
            <div className="stat-value">{agg.hit_rate_15pct}%</div>
            <div className="stat-sub">{agg.hits_15pct} / {agg.total_calls}</div>
          </div>
          <div className="stat-card hit-monster">
            <div className="stat-label">30%+ Monster</div>
            <div className="stat-value">{agg.hit_rate_30pct}%</div>
            <div className="stat-sub">{agg.hits_30pct} / {agg.total_calls}</div>
          </div>
          <div className="stat-card avg">
            <div className="stat-label">Avg Predicted</div>
            <div className="stat-value">{fmtPct(agg.avg_pred)}</div>
            <div className="stat-sub">10d max upside</div>
          </div>
          <div className="stat-card avg-actual">
            <div className="stat-label">Avg Actual</div>
            <div className="stat-value">{fmtPct(agg.avg_actual)}</div>
            <div className="stat-sub">forward 7d max</div>
          </div>
        </div>
      )}

      {/* Tab nav */}
      <div className="monsters-tabs">
        <button className={activeView === 'live' ? 'active' : ''} onClick={() => setActiveView('live')}>
          📈 Today's Picks ({live.length})
        </button>
        <button className={activeView === 'history' ? 'active' : ''} onClick={() => setActiveView('history')}>
          📊 History ({history.length})
        </button>
        <button className={activeView === 'weekly' ? 'active' : ''} onClick={() => setActiveView('weekly')}>
          📅 Weekly ({weekly.length})
        </button>
      </div>

      {/* Live view */}
      {activeView === 'live' && (
        <div className="view-live">
          <h3>Calls — predicted upside (regression model)</h3>
          <table className="monsters-table">
            <thead>
              <tr>
                <th>#</th><th>Ticker</th><th>Pred Up%</th><th>Tilt</th><th>Score</th>
                <th>News</th><th>DTE</th><th>PT %</th><th>PreM %</th><th>RSI</th>
              </tr>
            </thead>
            <tbody>
              {liveCalls.map((p, i) => (
                <tr key={i} className={parseFloat(p.pred_up_pct) >= 20 ? 'high-conv' : ''}>
                  <td>{i + 1}</td>
                  <td className="ticker">{p.ticker}</td>
                  <td className="pos">{fmtPct(p.pred_up_pct)}</td>
                  <td>{p.tilt || 0}</td>
                  <td>{parseFloat(p.tilted_score || 0).toFixed(1)}</td>
                  <td>{p.has_news === 'True' || p.has_news === true ? 'Y' : 'n'}</td>
                  <td>{p.days_to_earnings || '—'}</td>
                  <td className={cls(parseFloat(p.pt_upside))}>{fmtPct(p.pt_upside)}</td>
                  <td className={cls(parseFloat(p.premarket_gap))}>{fmtPct(p.premarket_gap)}</td>
                  <td>{p.rsi ? Math.round(parseFloat(p.rsi)) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3 style={{marginTop: '2rem'}}>Puts — P(put) (classification model)</h3>
          <table className="monsters-table">
            <thead>
              <tr>
                <th>#</th><th>Ticker</th><th>P(put)</th><th>News</th>
                <th>DTE</th><th>Gap%</th><th>VolZ</th><th>RSI</th>
              </tr>
            </thead>
            <tbody>
              {livePuts.map((p, i) => (
                <tr key={i} className={parseFloat(p.p_put || 0) >= 0.5 ? 'high-conv' : ''}>
                  <td>{i + 1}</td>
                  <td className="ticker">{p.ticker}</td>
                  <td>{parseFloat(p.p_put || 0).toFixed(3)}</td>
                  <td>{p.has_news === 'True' || p.has_news === true ? 'Y' : 'n'}</td>
                  <td>{p.days_to_earnings || '—'}</td>
                  <td className={cls(parseFloat(p.gap_pct))}>{fmtPct(p.gap_pct)}</td>
                  <td className={cls(parseFloat(p.vol_zscore))}>{parseFloat(p.vol_zscore || 0).toFixed(1)}</td>
                  <td>{p.rsi ? Math.round(parseFloat(p.rsi)) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* History view */}
      {activeView === 'history' && (
        <div className="view-history">
          <div className="filter-row">
            <button className={historyFilter === 'all' ? 'active' : ''} onClick={() => setHistoryFilter('all')}>All</button>
            <button className={historyFilter === 'wins5' ? 'active' : ''} onClick={() => setHistoryFilter('wins5')}>5%+ Wins</button>
            <button className={historyFilter === 'wins15' ? 'active' : ''} onClick={() => setHistoryFilter('wins15')}>15%+ Wins</button>
            <button className={historyFilter === 'monsters' ? 'active' : ''} onClick={() => setHistoryFilter('monsters')}>30%+ Monsters</button>
            <button className={historyFilter === 'losses' ? 'active' : ''} onClick={() => setHistoryFilter('losses')}>Losses</button>
            <span className="filter-count">{filteredHistory.length} picks</span>
          </div>
          <table className="monsters-table">
            <thead>
              <tr>
                <th>Date</th><th>Ticker</th><th>Pred Up</th><th>Actual 7d</th><th>Hit?</th><th>Est Opt P&L</th>
              </tr>
            </thead>
            <tbody>
              {filteredHistory.slice(0, 500).map((r, i) => {
                const actual = parseFloat(r.out_max_up_7d || 0);
                return (
                  <tr key={i}>
                    <td>{r.date}</td>
                    <td className="ticker">{r.ticker}</td>
                    <td className="pos">{fmtPct(r.pred_up_pct)}</td>
                    <td className={cls(actual)}>{fmtPct(actual)}</td>
                    <td>{actual >= 30 ? '🐲' : actual >= 15 ? '✅' : actual >= 5 ? '✓' : actual >= 0 ? '·' : '✗'}</td>
                    <td className={cls(parseFloat(r.option_pnl_est_pct))}>{fmtPct(r.option_pnl_est_pct)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredHistory.length > 500 && (
            <div className="trunc-note">Showing first 500 of {filteredHistory.length}</div>
          )}
        </div>
      )}

      {/* Weekly view */}
      {activeView === 'weekly' && (
        <div className="view-weekly">
          <table className="monsters-table">
            <thead>
              <tr>
                <th>Week</th><th>Picks</th><th>5%+ Hit</th><th>15%+ Hit</th><th>Monsters</th>
                <th>Avg Pred</th><th>Avg Actual</th><th>Best Pick</th><th>Worst Pick</th>
              </tr>
            </thead>
            <tbody>
              {weekly.map((w, i) => {
                const hit5 = (w.call_5pct_hits / Math.max(1, w.call_n) * 100).toFixed(0);
                const hit15 = (w.call_15pct_hits / Math.max(1, w.call_n) * 100).toFixed(0);
                const hitM = (w.call_monster_hits / Math.max(1, w.call_n) * 100).toFixed(0);
                return (
                  <tr key={i}>
                    <td>{w.week}</td>
                    <td>{w.call_n}</td>
                    <td className={parseInt(hit5) >= 80 ? 'pos' : ''}>{hit5}%</td>
                    <td className={parseInt(hit15) >= 60 ? 'pos' : ''}>{hit15}%</td>
                    <td className={parseInt(hitM) >= 20 ? 'pos' : ''}>{hitM}%</td>
                    <td>{fmtPct(w.avg_call_pred)}</td>
                    <td className={cls(w.avg_call_actual)}>{fmtPct(w.avg_call_actual)}</td>
                    <td>{w.best_pick ? `${w.best_pick.ticker} ${fmtPct(w.best_pick.fwd)}` : '—'}</td>
                    <td>{w.worst_pick ? `${w.worst_pick.ticker} ${fmtPct(w.worst_pick.fwd)}` : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default MonstersDashboard;
